/* MidYearCutoverWizard — the killer onboarding flow for clients joining
 * mid-year. Four sequential steps, each independent:
 *   1. Pick cutover date (defaults to last day of last month)
 *   2. Upload QuickBooks Trial Balance → preview → AI-map → commit
 *   3. Upload Transaction Detail since cutover → preview → commit
 *   4. (Optional) Upload prior-year P&L + BS for comparative dashboards
 *
 * No double-entry: TB creates ONE opening JE; transactions replay only
 * from cutover-date+1 onward; Plaid syncs new ones automatically.
 *
 * Each step's "Commit" call is idempotent server-side (TB by date,
 * transactions by content-hash) so re-running on a bad upload is safe.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { apiFetch } from '../../fetchInterceptor';
import { toast } from 'sonner';
import {
  ArrowRight, Calendar, Upload, Sparkles, Check, Loader2,
  AlertCircle, FileSpreadsheet, ArrowRightLeft, TrendingUp,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'date', label: 'Cutover date', icon: Calendar },
  { id: 'tb', label: 'Trial Balance', icon: FileSpreadsheet },
  { id: 'txn', label: 'Transactions', icon: ArrowRightLeft },
  { id: 'compare', label: 'Comparative (optional)', icon: TrendingUp },
  { id: 'done', label: 'Done', icon: Check },
];

function lastDayOfPriorMonth() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), 0);
  return d.toISOString().slice(0, 10);
}

export default function MidYearCutoverWizard({ workspaceId }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [cutoverDate, setCutoverDate] = useState(lastDayOfPriorMonth());

  // Step 2 — Trial Balance state
  const tbFileRef = useRef(null);
  const [tbPreview, setTbPreview] = useState(null);   // { preview, total_debit, total_credit, balanced }
  const [tbMapping, setTbMapping] = useState({});      // { qb_account: { account_id, ... } }
  const [tbCommitting, setTbCommitting] = useState(false);
  const [tbBusy, setTbBusy] = useState(false);

  // Step 3 — Transactions state
  const txnFileRef = useRef(null);
  const [txnPreview, setTxnPreview] = useState(null);
  const [txnBusy, setTxnBusy] = useState(false);
  const [txnCommitting, setTxnCommitting] = useState(false);
  const [txnMapping, setTxnMapping] = useState({});    // { qb_account: account_id }

  // Step 4 — Comparative state
  const compFileRef = useRef(null);
  const [compType, setCompType] = useState('pnl');
  const [compYear, setCompYear] = useState(new Date().getFullYear() - 1);
  const [compBusy, setCompBusy] = useState(false);

  // CountBucks accounts (loaded once for dropdown options)
  const [cbAccounts, setCbAccounts] = useState([]);
  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/accounts`);
        if (r.ok) {
          const d = await r.json();
          setCbAccounts(d || []);
        }
      } catch { /* ignore */ }
    })();
  }, [workspaceId]);

  const accountOptions = useMemo(() => {
    return cbAccounts.map((a) => ({
      value: a.account_id,
      label: `${a.code || ''} · ${a.name} (${a.type})`.trim(),
    }));
  }, [cbAccounts]);

  // ── Step 2: Trial Balance ─────────────────────────────────────────
  const uploadTb = async () => {
    const file = tbFileRef.current?.files?.[0];
    if (!file) {
      toast.error('Pick a Trial Balance CSV first');
      return;
    }
    setTbBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('workspace_id', workspaceId);
      const r = await apiFetch(`${API_URL}/api/migration/import/trial-balance`, {
        method: 'POST', body: fd,
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Trial Balance parse failed');
        return;
      }
      setTbPreview(d);
      // Seed mapping with the AI fuzzy suggestions.
      const initial = {};
      (d.preview || []).forEach((row) => {
        if (row.suggested_account_id) {
          initial[row.qb_account] = {
            account_id: row.suggested_account_id,
            account_name: row.suggested_account_name,
            confidence: row.confidence,
          };
        }
      });
      setTbMapping(initial);
      // Run AI mapping to upgrade weak matches + fill gaps.
      const unmapped = (d.preview || [])
        .filter((r) => !r.suggested_account_id || r.confidence < 0.85)
        .map((r) => r.qb_account);
      if (unmapped.length > 0) {
        try {
          const aiR = await apiFetch(`${API_URL}/api/migration/coa-suggest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ workspace_id: workspaceId, qb_account_names: unmapped }),
          });
          const aiD = await aiR.json();
          if (aiR.ok) {
            const aiMap = { ...initial };
            (aiD.mappings || []).forEach((m) => {
              if (m.account_id) {
                aiMap[m.qb_account] = {
                  account_id: m.account_id,
                  account_name: m.account_name,
                  confidence: m.confidence,
                  source: m.source,
                };
              }
            });
            setTbMapping(aiMap);
            toast.success(`AI mapped ${aiD.mappings.filter((m) => m.account_id).length} accounts`);
          }
        } catch { /* ignore — fuzzy still works */ }
      }
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    }
    setTbBusy(false);
  };

  const commitTb = async () => {
    if (!tbPreview) return;
    if (!tbPreview.balanced) {
      toast.error("Trial balance doesn't balance — fix the CSV or contact support");
      return;
    }
    const lines = (tbPreview.preview || []).map((row) => ({
      account_id: tbMapping[row.qb_account]?.account_id,
      debit: row.debit, credit: row.credit, qb_account: row.qb_account,
    })).filter((l) => l.account_id);

    const unmappedCount = (tbPreview.preview || []).length - lines.length;
    if (unmappedCount > 0) {
      if (!window.confirm(`${unmappedCount} account(s) are unmapped and will be SKIPPED. Continue?`)) return;
    }
    setTbCommitting(true);
    try {
      const r = await apiFetch(`${API_URL}/api/migration/import/trial-balance/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          cutover_date: cutoverDate,
          lines,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Commit failed');
        return;
      }
      toast.success(`Opening JE posted · ${d.lines_posted} lines · $${d.total_debit.toFixed(2)} Dr / $${d.total_credit.toFixed(2)} Cr`);
      setStepIdx(2);
    } catch (e) {
      toast.error(e.message || 'Network error');
    }
    setTbCommitting(false);
  };

  // ── Step 3: Transactions ──────────────────────────────────────────
  const uploadTxn = async () => {
    const file = txnFileRef.current?.files?.[0];
    if (!file) {
      toast.error('Pick a Transaction Detail CSV first');
      return;
    }
    setTxnBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('workspace_id', workspaceId);
      const r = await apiFetch(`${API_URL}/api/migration/import/transactions`, {
        method: 'POST', body: fd,
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Parse failed');
        return;
      }
      setTxnPreview(d);

      // AI-map unique account names found in the file.
      const uniqAccounts = [...new Set((d.preview || []).map((p) => p.account).filter(Boolean))];
      if (uniqAccounts.length > 0) {
        try {
          const aiR = await apiFetch(`${API_URL}/api/migration/coa-suggest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ workspace_id: workspaceId, qb_account_names: uniqAccounts }),
          });
          const aiD = await aiR.json();
          if (aiR.ok) {
            const m = {};
            (aiD.mappings || []).forEach((row) => {
              if (row.account_id) m[row.qb_account] = row.account_id;
            });
            setTxnMapping(m);
          }
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    }
    setTxnBusy(false);
  };

  const commitTxn = async () => {
    if (!txnPreview) return;
    const lines = (txnPreview.preview || []).map((row) => ({
      txn_date: row.date,
      description: row.description,
      amount: row.amount,
      account_id: txnMapping[row.account],
    })).filter((l) => l.account_id && l.txn_date && l.amount);

    if (lines.length === 0) {
      toast.error('No mapped lines to commit. Map accounts first.');
      return;
    }
    setTxnCommitting(true);
    try {
      const r = await apiFetch(`${API_URL}/api/migration/import/transactions/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, lines }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Commit failed');
        return;
      }
      toast.success(`${d.posted} JEs posted · ${d.skipped_duplicates} duplicates skipped`);
      setStepIdx(3);
    } catch (e) {
      toast.error(e.message || 'Network error');
    }
    setTxnCommitting(false);
  };

  // ── Step 4: Comparative ───────────────────────────────────────────
  const uploadComparative = async () => {
    const file = compFileRef.current?.files?.[0];
    if (!file) {
      toast.error('Pick a CSV first');
      return;
    }
    setCompBusy(true);
    try {
      // Parse CSV client-side — comparative is just label/amount pairs.
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const rows = lines.map((line) => {
        const parts = line.split(',');
        // Heuristic: first column = label, last numeric column = amount.
        const label = (parts[0] || '').trim().replace(/^"|"$/g, '');
        const amount = parts.slice(1).reverse().find((p) => /[\d.]/.test(p));
        return { label, amount: amount || '0' };
      }).filter((r) => r.label && !/^total/i.test(r.label));

      const r = await apiFetch(`${API_URL}/api/migration/import/comparative`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          statement_type: compType,
          period_year: parseInt(compYear, 10),
          rows,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Comparative save failed');
        return;
      }
      toast.success(`Saved ${d.saved_rows} rows for ${compType.toUpperCase()} ${compYear}`);
      setStepIdx(4);
    } catch (e) {
      toast.error(e.message || 'Error');
    }
    setCompBusy(false);
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Card data-testid="cutover-wizard">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-500" />
          Mid-Year Cutover Wizard
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          For clients joining mid-year. Get your CountBucks books to match
          QuickBooks in under 20 minutes — without re-typing anything you
          already entered there.
        </p>
        {/* Stepper */}
        <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < stepIdx;
            const active = i === stepIdx;
            return (
              <div
                key={s.id}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs whitespace-nowrap',
                  done && 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30',
                  active && 'bg-brand-500/10 text-brand-600 border border-brand-500/40 font-semibold',
                  !done && !active && 'bg-muted text-muted-foreground border border-transparent',
                )}
                data-testid={`cutover-step-${s.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {s.label}
                {done && <Check className="w-3.5 h-3.5" />}
              </div>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* ── Step 1: Date picker ────────────────────────────────── */}
        {stepIdx === 0 && (
          <div className="space-y-4" data-testid="cutover-step-date-panel">
            <Label className="text-sm font-medium">Pick your cutover date</Label>
            <p className="text-xs text-muted-foreground">
              Everything BEFORE this date is summarized as one opening
              journal entry. Everything FROM this date onward will be
              replayed transaction-by-transaction in step 3. Best practice:
              use the last day of last month so the month boundary is
              clean.
            </p>
            <Input
              type="date"
              value={cutoverDate}
              onChange={(e) => setCutoverDate(e.target.value)}
              className="max-w-xs"
              data-testid="cutover-date-input"
            />
            <Button
              onClick={() => setStepIdx(1)}
              data-testid="cutover-date-next-btn"
              className="bg-brand-500 hover:bg-brand-400 text-black font-semibold"
            >
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* ── Step 2: Trial Balance ──────────────────────────────── */}
        {stepIdx === 1 && (
          <div className="space-y-4" data-testid="cutover-step-tb-panel">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Upload Trial Balance CSV</Label>
              <p className="text-xs text-muted-foreground">
                Export from QuickBooks → Reports → Trial Balance.
                Set the date to <span className="font-mono">{cutoverDate}</span>.
                Save as CSV and upload here.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                ref={tbFileRef}
                type="file"
                accept=".csv"
                data-testid="cutover-tb-file"
                className="max-w-md"
              />
              <Button
                onClick={uploadTb}
                disabled={tbBusy}
                data-testid="cutover-tb-upload-btn"
              >
                {tbBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Preview
              </Button>
            </div>

            {tbPreview && (
              <div className="rounded-lg border p-3 space-y-3" data-testid="cutover-tb-preview">
                <div className="flex items-center justify-between text-xs">
                  <span>
                    Parsed <strong>{tbPreview.parsed_count}</strong> accounts ·{' '}
                    Total Dr <strong>${tbPreview.total_debit.toFixed(2)}</strong> ·{' '}
                    Total Cr <strong>${tbPreview.total_credit.toFixed(2)}</strong>
                  </span>
                  <Badge variant={tbPreview.balanced ? 'default' : 'destructive'} data-testid="cutover-tb-balance-badge">
                    {tbPreview.balanced ? 'Balanced' : 'Out of balance'}
                  </Badge>
                </div>

                <div className="max-h-96 overflow-y-auto border rounded text-xs">
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5">QB Account</th>
                        <th className="text-right px-2 py-1.5">Debit</th>
                        <th className="text-right px-2 py-1.5">Credit</th>
                        <th className="text-left px-2 py-1.5">Maps to CountBucks Account</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tbPreview.preview || []).map((row, i) => {
                        const sel = tbMapping[row.qb_account];
                        return (
                          <tr key={i} className="border-t" data-testid={`cutover-tb-row-${i}`}>
                            <td className="px-2 py-1 font-mono">{row.qb_account}</td>
                            <td className="px-2 py-1 text-right">{row.debit ? `$${row.debit.toFixed(2)}` : ''}</td>
                            <td className="px-2 py-1 text-right">{row.credit ? `$${row.credit.toFixed(2)}` : ''}</td>
                            <td className="px-2 py-1">
                              <select
                                value={sel?.account_id || ''}
                                onChange={(e) => setTbMapping({
                                  ...tbMapping,
                                  [row.qb_account]: {
                                    account_id: e.target.value,
                                    account_name: accountOptions.find((o) => o.value === e.target.value)?.label,
                                  },
                                })}
                                className="w-full bg-background border rounded px-1 py-0.5 text-xs"
                                data-testid={`cutover-tb-select-${i}`}
                              >
                                <option value="">— skip —</option>
                                {accountOptions.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              {sel?.confidence > 0 && sel.confidence < 0.85 && (
                                <span className="text-amber-600 text-[10px] block mt-0.5">
                                  AI confidence: {Math.round(sel.confidence * 100)}% — please verify
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <Button
                  onClick={commitTb}
                  disabled={tbCommitting || !tbPreview.balanced}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold"
                  data-testid="cutover-tb-commit-btn"
                >
                  {tbCommitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Post Opening Balance & Continue
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Transactions ───────────────────────────────── */}
        {stepIdx === 2 && (
          <div className="space-y-4" data-testid="cutover-step-txn-panel">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Upload Transaction Detail CSV</Label>
              <p className="text-xs text-muted-foreground">
                Export QuickBooks → Reports → Transaction Detail by Account.
                Set the date range from <span className="font-mono">{cutoverDate}</span> through today.
                Save as CSV and upload.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                ref={txnFileRef}
                type="file"
                accept=".csv"
                data-testid="cutover-txn-file"
                className="max-w-md"
              />
              <Button
                onClick={uploadTxn}
                disabled={txnBusy}
                data-testid="cutover-txn-upload-btn"
              >
                {txnBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Preview
              </Button>
            </div>

            {txnPreview && (
              <div className="rounded-lg border p-3 space-y-3" data-testid="cutover-txn-preview">
                <p className="text-xs">
                  Parsed <strong>{txnPreview.parsed_count}</strong> transactions
                  {txnPreview.error_count > 0 && (
                    <span className="text-amber-600"> · {txnPreview.error_count} errors</span>
                  )}
                </p>
                <div className="max-h-72 overflow-y-auto border rounded text-xs">
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5">Date</th>
                        <th className="text-left px-2 py-1.5">Description</th>
                        <th className="text-right px-2 py-1.5">Amount</th>
                        <th className="text-left px-2 py-1.5">QB Account</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(txnPreview.preview || []).slice(0, 50).map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 font-mono">{row.date}</td>
                          <td className="px-2 py-1 truncate max-w-xs" title={row.description}>{row.description}</td>
                          <td className="px-2 py-1 text-right">${row.amount.toFixed(2)}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">
                            {row.account}
                            {txnMapping[row.account] ? (
                              <span className="text-emerald-500 ml-1">✓</span>
                            ) : (
                              <span className="text-amber-500 ml-1">?</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Showing first 50 of {txnPreview.parsed_count}. Unmapped accounts
                  go to a temporary &quot;QB Migration Suspense&quot; account — you can
                  re-categorize from Banking after import.
                </p>
                <Button
                  onClick={commitTxn}
                  disabled={txnCommitting}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold"
                  data-testid="cutover-txn-commit-btn"
                >
                  {txnCommitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Post {txnPreview.parsed_count} Transactions
                </Button>
              </div>
            )}
            <Button variant="ghost" onClick={() => setStepIdx(3)} size="sm" data-testid="cutover-txn-skip-btn">
              Skip — I&apos;ll bring in transactions later from bank feeds →
            </Button>
          </div>
        )}

        {/* ── Step 4: Comparative ────────────────────────────────── */}
        {stepIdx === 3 && (
          <div className="space-y-4" data-testid="cutover-step-compare-panel">
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                Optional — upload prior-year P&amp;L or Balance Sheet for &quot;vs last year&quot; comparisons
              </Label>
              <p className="text-xs text-muted-foreground">
                These are stored separately and don&apos;t affect your current
                books. They power the YoY tile on the dashboard.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 max-w-xl">
              <div>
                <Label className="text-xs">Statement type</Label>
                <select
                  value={compType}
                  onChange={(e) => setCompType(e.target.value)}
                  className="w-full bg-background border rounded px-2 py-1.5 text-sm mt-1"
                  data-testid="cutover-comp-type-select"
                >
                  <option value="pnl">P&L (Profit & Loss)</option>
                  <option value="balance_sheet">Balance Sheet</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Year</Label>
                <Input
                  type="number"
                  value={compYear}
                  onChange={(e) => setCompYear(e.target.value)}
                  data-testid="cutover-comp-year-input"
                  className="mt-1"
                />
              </div>
              <div className="flex items-end">
                <Input
                  ref={compFileRef}
                  type="file"
                  accept=".csv"
                  data-testid="cutover-comp-file"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={uploadComparative}
                disabled={compBusy}
                data-testid="cutover-comp-upload-btn"
                className="bg-brand-500 hover:bg-brand-400 text-black"
              >
                {compBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Save comparative
              </Button>
              <Button variant="ghost" onClick={() => setStepIdx(4)} data-testid="cutover-comp-skip-btn">
                Skip
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 5: Done ───────────────────────────────────────── */}
        {stepIdx === 4 && (
          <div className="text-center py-8 space-y-3" data-testid="cutover-step-done-panel">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-600 mx-auto flex items-center justify-center">
              <Check className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-semibold">Migration complete</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Your CountBucks books now match QuickBooks as of {cutoverDate}.
              Connect a bank account in Banking → Connect to keep new
              transactions flowing in automatically.
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => window.location.href = '/dashboard'} className="bg-emerald-500 hover:bg-emerald-400 text-white" data-testid="cutover-done-dashboard-btn">
                Open dashboard
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/banking'} data-testid="cutover-done-banking-btn">
                Connect a bank
              </Button>
            </div>
          </div>
        )}

        {/* Errors / fallback */}
        {!workspaceId && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Select a workspace first.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
